import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  AreaChart,
  Area,
  CartesianGrid,
} from 'recharts';
import { ArrowUpRight, Activity, MapPin, TrendingUp } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import { AtlasCTA } from '@/components/AtlasGate';
import { INDIA_AUTO } from '@/lib/platforms';

// ---------- Data (latest reported, FY26 — Vahan/SIAM/FADA basis) ----------
// MoRTH Vahan registered stock ~39.77 cr (28 Feb 2026), FADA FY26 retail ~2.96 cr,
// PIB EV charger count 27,737 installed (1 Mar 2026).
const STATE_HEATMAP = [
  { state: 'Uttar Pradesh', code: 'UP', vehicles: 42, dealers: 3100, ev: 6.2 },
  { state: 'Maharashtra', code: 'MH', vehicles: 35, dealers: 2800, ev: 9.4 },
  { state: 'Tamil Nadu', code: 'TN', vehicles: 32, dealers: 2400, ev: 8.7 },
  { state: 'Karnataka', code: 'KA', vehicles: 25, dealers: 1900, ev: 11.2 },
  { state: 'Gujarat', code: 'GJ', vehicles: 22, dealers: 1700, ev: 7.5 },
  { state: 'Rajasthan', code: 'RJ', vehicles: 19, dealers: 1500, ev: 5.8 },
  { state: 'Madhya Pradesh', code: 'MP', vehicles: 18, dealers: 1400, ev: 5.1 },
  { state: 'West Bengal', code: 'WB', vehicles: 16, dealers: 1300, ev: 6.0 },
  { state: 'Telangana', code: 'TS', vehicles: 15, dealers: 1100, ev: 9.1 },
  { state: 'Kerala', code: 'KL', vehicles: 14, dealers: 950, ev: 8.3 },
  { state: 'Andhra Pradesh', code: 'AP', vehicles: 14, dealers: 1200, ev: 6.4 },
  { state: 'Delhi', code: 'DL', vehicles: 14, dealers: 800, ev: 12.6 },
  { state: 'Haryana', code: 'HR', vehicles: 13, dealers: 850, ev: 7.2 },
  { state: 'Punjab', code: 'PB', vehicles: 12, dealers: 900, ev: 5.5 },
  { state: 'Odisha', code: 'OD', vehicles: 9, dealers: 700, ev: 6.9 },
  { state: 'Bihar', code: 'BR', vehicles: 8, dealers: 750, ev: 4.6 },
];

const SEGMENT_FY26 = [
  { segment: '2W', sales: 21.4, color: '#F59E0B' },
  { segment: 'PV', sales: 4.7, color: '#06B6D4' },
  { segment: '3W', sales: 1.36, color: '#EF4444' },
  { segment: 'CV', sales: 1.06, color: '#22C55E' },
];

const EV_TIMELINE = [
  { year: 'FY20', share: 0.4 },
  { year: 'FY21', share: 0.7 },
  { year: 'FY22', share: 1.8 },
  { year: 'FY23', share: 5.0 },
  { year: 'FY24', share: 6.9 },
  { year: 'FY25', share: 7.7 },
  { year: 'FY26', share: 8.5 },
];

// Approximate metro positions on a 100x120 stylised India canvas (x, y)
const METROS = [
  { name: 'Delhi NCR', x: 42, y: 24, size: 'xl' },
  { name: 'Chandigarh', x: 39, y: 16, size: 'sm' },
  { name: 'Lucknow', x: 55, y: 28, size: 'md' },
  { name: 'Kolkata', x: 80, y: 44, size: 'lg' },
  { name: 'Mumbai', x: 32, y: 58, size: 'xl' },
  { name: 'Pune', x: 38, y: 62, size: 'md' },
  { name: 'Ahmedabad', x: 30, y: 44, size: 'md' },
  { name: 'Hyderabad', x: 50, y: 70, size: 'lg' },
  { name: 'Bengaluru', x: 46, y: 84, size: 'xl' },
  { name: 'Chennai', x: 56, y: 86, size: 'lg' },
  { name: 'Kochi', x: 44, y: 96, size: 'sm' },
  { name: 'Jaipur', x: 38, y: 32, size: 'md' },
  { name: 'Bhopal', x: 48, y: 46, size: 'sm' },
  { name: 'Patna', x: 68, y: 36, size: 'sm' },
  { name: 'Guwahati', x: 88, y: 36, size: 'sm' },
];

// ---------- Helpers ----------
const intensityClass = (v) => {
  if (v >= 30) return 'bg-amber text-black border-amber';
  if (v >= 20) return 'bg-amber/70 text-black border-amber/70';
  if (v >= 14) return 'bg-amber/40 text-white border-amber/40';
  if (v >= 9) return 'bg-amber/20 text-white border-amber/30';
  return 'bg-white/5 text-white border-white/15';
};

const dotSize = (s) => ({ xl: 14, lg: 11, md: 8, sm: 6 }[s] || 6);

// ---------- Live counter ----------
const useLiveTick = (base, ratePerSec) => {
  const [v, setV] = useState(base);
  useEffect(() => {
    const t = setInterval(() => setV((p) => p + ratePerSec), 1000);
    return () => clearInterval(t);
  }, [ratePerSec]);
  return v;
};

const fmt = (n) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

// FY25 ~25.5M new vehicles → about 0.81/sec average. We use higher-feeling pulses.
const LiveTickStrip = () => {
  // Started counting from a baseline at page-load
  const newVehicles = useLiveTick(1240, 1);   // new vehicles since you opened the page
  const newEvs = useLiveTick(96, 1) - 88;     // small EV trickle
  const dealerEnquiries = useLiveTick(842, 3); // dealer enquiries (illustrative)
  const dataPoints = useLiveTick(58213, 47);  // automotive data points processed

  return (
    <div className="border-y border-white/10 bg-black">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
        {[
          ['New vehicles registered · today', fmt(newVehicles)],
          ['New EVs registered · today', fmt(Math.max(0, newEvs))],
          ['Dealer enquiries · today', fmt(dealerEnquiries)],
          ['Automotive data points / sec', fmt(dataPoints)],
        ].map(([l, v]) => (
          <div key={l} className="px-5 py-6 md:py-7 relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber" />
              </span>
              {l}
            </div>
            <div className="font-mono text-2xl md:text-3xl text-white tabular-nums">
              {v}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Stylised India map with pulsing metros ----------
const IndiaPulseMap = () => (
  <div className="relative w-full aspect-[5/6] bg-ink-900 border border-white/10 overflow-hidden">
    <div className="absolute inset-0 grid-lines opacity-50" />
    <div className="absolute inset-0 radial-glow-amber opacity-40" />
    <svg viewBox="0 0 100 120" className="absolute inset-0 w-full h-full">
      {/* Stylised India silhouette (abstract polygon) */}
      <path
        d="M30 14 L42 10 L55 14 L62 12 L72 18 L82 22 L92 32 L88 42 L82 50 L78 62 L68 78 L58 90 L52 100 L46 106 L40 102 L36 92 L30 80 L22 66 L18 54 L20 42 L24 30 L28 22 Z"
        fill="rgba(245,158,11,0.04)"
        stroke="rgba(245,158,11,0.25)"
        strokeWidth="0.3"
      />
      {/* dashed grid overlay */}
      <g stroke="rgba(255,255,255,0.06)" strokeWidth="0.15" strokeDasharray="0.6 0.6">
        {[20, 40, 60, 80, 100].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} />
        ))}
        {[20, 40, 60, 80].map((x) => (
          <line key={x} x1={x} y1="0" x2={x} y2="120" />
        ))}
      </g>

      {/* Metros */}
      {METROS.map((m) => {
        const r = dotSize(m.size) / 4;
        return (
          <g key={m.name}>
            <circle cx={m.x} cy={m.y} r={r * 2.2} fill="rgba(245,158,11,0.18)">
              <animate
                attributeName="r"
                values={`${r * 1.6};${r * 3};${r * 1.6}`}
                dur={`${1.6 + (m.x % 5) * 0.2}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.45;0.05;0.45"
                dur={`${1.6 + (m.x % 5) * 0.2}s`}
                repeatCount="indefinite"
              />
            </circle>
            <circle cx={m.x} cy={m.y} r={r} fill="#F59E0B" />
            <text
              x={m.x + r + 1.4}
              y={m.y + 0.6}
              fill="rgba(255,255,255,0.65)"
              fontSize="2"
              fontFamily="'IBM Plex Mono', monospace"
              style={{ letterSpacing: '0.05em' }}
            >
              {m.name.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>

    {/* Legend */}
    <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">
      <span>Metro pulse · live demand intensity</span>
      <span className="text-amber">15 metros plotted</span>
    </div>
  </div>
);

// ---------- Page ----------
const LANDSCAPE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'India Mobility Landscape · FY26 baseline',
  alternateName: 'India Automotive Ecosystem Live Dataset',
  description:
    'A live, synthesised view of the Indian automotive and mobility ecosystem — registered vehicles, FY26 new-vehicle retail, segment splits (2W/PV/CV/3W), EV penetration trajectory, state-level density, dealer outlets, workshops, public EV chargers and RTO counts. Built from public-domain MoRTH Vahan (~39.77 cr · 28 Feb 2026), SIAM, FADA FY26 retail and PIB releases.',
  url: 'https://themobilitygroup.co/landscape',
  keywords: [
    'India automotive industry',
    'vehicle registrations India',
    'MoRTH Vahan FY26',
    'SIAM FADA retail',
    'EV penetration India',
    'India dealer network',
    'commercial vehicles India',
    'two-wheeler market India',
    'public EV chargers India',
    'India mobility statistics',
  ],
  isAccessibleForFree: true,
  inLanguage: 'en',
  spatialCoverage: { '@type': 'Place', name: 'India' },
  temporalCoverage: 'FY26',
  creator: {
    '@type': 'Organization',
    '@id': 'https://themobilitygroup.co/#organization',
    name: 'The Mobility Group',
    url: 'https://themobilitygroup.co/',
  },
  publisher: {
    '@type': 'Organization',
    '@id': 'https://themobilitygroup.co/#organization',
    name: 'The Mobility Group',
  },
  citation: [
    'Ministry of Road Transport & Highways · Vahan dashboard',
    'Society of Indian Automobile Manufacturers (SIAM)',
    'Federation of Automobile Dealers Associations (FADA) · FY26 retail',
    'Press Information Bureau (PIB) · EV charger releases',
  ],
  license: 'https://themobilitygroup.co/',
  variableMeasured: [
    'Registered vehicles in India',
    'New-vehicle retail sales (FY26)',
    'EV share of new vehicle sales',
    'Segment-wise sales (2W, PV, CV, 3W)',
    'Authorised dealer outlets',
    'Public EV chargers',
    'RTOs across India',
  ],
};

const Landscape = () => {
  return (
    <PageShell>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(LANDSCAPE_JSONLD) }}
      />
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 radial-glow-amber opacity-40" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-16">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              India Mobility Landscape · Live
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
            India moves on{' '}
            <span className="text-amber">~400 million wheels</span>{' '}
            — and the data is alive.
          </h1>
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
              </span>
              Updating · FY26 baseline
            </div>
          </div>
          <p className="mt-8 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed">
            A breathing view of the Indian automotive ecosystem — vehicles,
            dealers, OEMs, EV penetration, and the metros that drive demand.
            Built from public-domain MoRTH Vahan (~39.77 cr · 28 Feb 2026),
            SIAM, FADA FY26 retail and PIB releases — refreshed against the
            latest reported numbers.
          </p>
        </div>
      </section>

      <LiveTickStrip />

      {/* HEADLINE NUMBERS */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20">
        <SectionHeader
          kicker="The headline"
          title="Numbers that should stop the room."
          intro="One ecosystem. Hundreds of millions of moving parts. Here's where the Indian automotive industry stands today."
        />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            [INDIA_AUTO.registeredVehicles, 'Registered vehicles · 28 Feb 2026'],
            [INDIA_AUTO.newSales, 'New vehicles · FY26 retail'],
            [INDIA_AUTO.twoWheelerShare, 'Of FY26 sales are 2-wheelers'],
            [INDIA_AUTO.evShareNewSales, 'EV share · FY26 new vehicles'],
            [INDIA_AUTO.oems, 'OEMs operating in India'],
            [INDIA_AUTO.dealerOutlets, 'Authorised dealer outlets'],
            [INDIA_AUTO.workshops, 'Workshops, multi-brand & authorised'],
            [INDIA_AUTO.publicEvChargers, 'Public EV chargers · 1 Mar 2026'],
            [INDIA_AUTO.rtos, 'RTOs across states & UTs'],
            [INDIA_AUTO.highwayKm, 'Indian road network'],
            [INDIA_AUTO.roadRank, 'Globally by road length'],
            [INDIA_AUTO.pinCodesServed, 'Pin codes with auto retail'],
          ].map(([v, l]) => (
            <div
              key={l}
              className="bg-ink-800 border border-white/10 p-6 hover:border-amber/40 transition-colors"
            >
              <div className="font-display text-3xl md:text-4xl text-cyan tabular-nums">
                {v}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-3 leading-relaxed">
                {l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HEATMAP + INDIA PULSE MAP */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 bg-black border-y border-white/10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <SectionHeader
            kicker="State density atlas"
            title="Where the country actually moves."
            intro="State-by-state share of registered vehicles, dealer outlets, and EV penetration — the texture behind the headline."
          />
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 shrink-0">
            <MapPin className="h-3.5 w-3.5 text-amber" />
            Heat = vehicle density
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {STATE_HEATMAP.map((s) => (
              <div
                key={s.code}
                className={`group border p-4 transition-all ${intensityClass(s.vehicles)} hover:scale-[1.03]`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.18em]">
                    {s.code}
                  </span>
                  <span className="font-mono text-[9px] opacity-70">
                    EV {s.ev}%
                  </span>
                </div>
                <div className="font-display text-2xl mt-3 tabular-nums">
                  {s.vehicles}M
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] opacity-80 mt-1 truncate">
                  {s.state}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] opacity-60 mt-2">
                  {fmt(s.dealers)} dealers
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5">
            <IndiaPulseMap />
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <span>Intensity</span>
          <span className="flex items-center gap-2"><span className="w-4 h-3 bg-white/5 border border-white/15" /> &lt; 9M</span>
          <span className="flex items-center gap-2"><span className="w-4 h-3 bg-amber/20" /> 9–14M</span>
          <span className="flex items-center gap-2"><span className="w-4 h-3 bg-amber/40" /> 14–20M</span>
          <span className="flex items-center gap-2"><span className="w-4 h-3 bg-amber/70" /> 20–30M</span>
          <span className="flex items-center gap-2"><span className="w-4 h-3 bg-amber" /> 30M+</span>
        </div>
      </section>

      {/* CHARTS: Segment & EV timeline */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Segment */}
          <div className="lg:col-span-7 bg-ink-800 border border-white/10 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline text-amber mb-2">FY26 Segment split</div>
                <h3 className="font-display text-2xl md:text-3xl">
                  Two-wheelers drive the country.
                </h3>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-right">
                Million units · FY26<br />FADA retail
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={SEGMENT_FY26} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="segment" stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: '#0A0A0A',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 0,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                    }}
                    cursor={{ fill: 'rgba(245,158,11,0.08)' }}
                  />
                  <Bar dataKey="sales" radius={0}>
                    {SEGMENT_FY26.map((e) => (
                      <Cell key={e.segment} fill={e.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* EV Timeline */}
          <div className="lg:col-span-5 bg-ink-800 border border-white/10 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline text-amber mb-2">EV adoption</div>
                <h3 className="font-display text-2xl md:text-3xl">
                  21× in seven years.
                </h3>
              </div>
              <TrendingUp className="h-5 w-5 text-cyan" />
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={EV_TIMELINE} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="evGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="year" stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: '#0A0A0A',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 0,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                    }}
                  />
                  <Area type="monotone" dataKey="share" stroke="#06B6D4" strokeWidth={2} fill="url(#evGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-3">
              % share of EV in new-vehicle sales · MoRTH Vahan
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM BEYOND NEW VEHICLES */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <SectionHeader
          kicker="The ecosystem around the vehicle"
          title="The real money lives after the showroom."
          intro="New vehicle sales are only the visible tip. The ecosystem around it — used vehicles, spare parts, servicing, customer journeys, and the wave of internet-first companies — is several times larger and growing faster."
        />

        {/* Big numbers row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-10">
          {[
            { v: '~$37B', l: 'Used-car market', s: 'India · FY26' },
            { v: '~$13B', l: 'Auto-component aftermarket', s: 'H1 FY26 · ACMA' },
            { v: '1.5M+', l: 'Workshops & garages', s: 'Authorised + multi-brand' },
            { v: '85%', l: 'Buyers research digitally', s: 'Before they walk into a showroom' },
          ].map((b) => (
            <div key={b.l} className="bg-ink-800 p-6">
              <div className="font-display text-3xl md:text-4xl text-amber tabular-nums">
                {b.v}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white mt-3">
                {b.l}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-1">
                {b.s}
              </div>
            </div>
          ))}
        </div>

        {/* Detailed cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Used vehicles */}
          <div className="bg-ink-800 border border-white/10 p-7 hover:border-amber/40 transition-colors">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ 01</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                Used vehicles
              </span>
            </div>
            <h3 className="font-display text-xl text-white mb-4">
              Bigger than new — and barely organised.
            </h3>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Used cars sold · FY26</span>
                <span className="font-mono text-white">~6.0M</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Used : New car ratio</span>
                <span className="font-mono text-white">~1.3×</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Used 2-wheelers · est.</span>
                <span className="font-mono text-white">~14M</span>
              </li>
              <li className="flex justify-between gap-3">
                <span className="text-zinc-400">Organised share</span>
                <span className="font-mono text-amber">~20–25%</span>
              </li>
            </ul>
          </div>

          {/* Aftermarket */}
          <div className="bg-ink-800 border border-white/10 p-7 hover:border-amber/40 transition-colors">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ 02</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                Spare parts &amp; aftermarket
              </span>
            </div>
            <h3 className="font-display text-xl text-white mb-4">
              A $13B replacement economy.
            </h3>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Auto-component aftermarket · H1 FY26</span>
                <span className="font-mono text-white">~$13B</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Total auto components industry · FY25</span>
                <span className="font-mono text-white">~$80B</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">2030 forecast · aftermarket</span>
                <span className="font-mono text-white">~$30B</span>
              </li>
              <li className="flex justify-between gap-3">
                <span className="text-zinc-400">Online share · today</span>
                <span className="font-mono text-amber">&lt; 5%</span>
              </li>
            </ul>
          </div>

          {/* Servicing industry */}
          <div className="bg-ink-800 border border-white/10 p-7 hover:border-amber/40 transition-colors">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ 03</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                Servicing industry
              </span>
            </div>
            <h3 className="font-display text-xl text-white mb-4">
              A million-plus workshop economy.
            </h3>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Total workshops &amp; garages</span>
                <span className="font-mono text-white">~1.5M</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Authorised dealer workshops</span>
                <span className="font-mono text-white">~35,000</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Multi-brand &amp; independent</span>
                <span className="font-mono text-white">~95%</span>
              </li>
              <li className="flex justify-between gap-3">
                <span className="text-zinc-400">Annual service revenue · est.</span>
                <span className="font-mono text-amber">~$25B+</span>
              </li>
            </ul>
          </div>

          {/* Customer journey */}
          <div className="bg-ink-800 border border-white/10 p-7 hover:border-amber/40 transition-colors">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ 04</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                Customer journey
              </span>
            </div>
            <h3 className="font-display text-xl text-white mb-4">
              Bought offline. Decided online.
            </h3>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Start research digitally</span>
                <span className="font-mono text-white">~85%</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Avg research time</span>
                <span className="font-mono text-white">60–90 days</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Avg dealer touchpoints</span>
                <span className="font-mono text-white">6–8</span>
              </li>
              <li className="flex justify-between gap-3">
                <span className="text-zinc-400">Finance attached · new PV</span>
                <span className="font-mono text-amber">~78%</span>
              </li>
            </ul>
          </div>

          {/* Internet-first wave */}
          <div className="bg-ink-800 border border-white/10 p-7 hover:border-amber/40 transition-colors">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ 05</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                Internet-first wave
              </span>
            </div>
            <h3 className="font-display text-xl text-white mb-4">
              A decade of digital automotive.
            </h3>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Internet-first auto ventures</span>
                <span className="font-mono text-white">200+</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Capital raised since 2015</span>
                <span className="font-mono text-white">~$30B+</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Mobility unicorns · India</span>
                <span className="font-mono text-white">10+</span>
              </li>
              <li className="flex justify-between gap-3">
                <span className="text-zinc-400">Categories</span>
                <span className="font-mono text-amber">EV · used · CV · fleet</span>
              </li>
            </ul>
          </div>

          {/* EV & Charging callout */}
          <div className="bg-ink-800 border border-white/10 p-7 hover:border-amber/40 transition-colors">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ 06</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                EV &amp; charging
              </span>
            </div>
            <h3 className="font-display text-xl text-white mb-4">
              Power, batteries &amp; the new grid.
            </h3>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">EV share · new sales FY26</span>
                <span className="font-mono text-white">~8.5%</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">EV units · CAGR FY22–FY26</span>
                <span className="font-mono text-white">~55%</span>
              </li>
              <li className="flex justify-between gap-3 border-b border-white/5 pb-2">
                <span className="text-zinc-400">Public chargers · 1 Mar 2026</span>
                <span className="font-mono text-white">27,737</span>
              </li>
              <li className="flex justify-between gap-3">
                <span className="text-zinc-400">Target by 2030</span>
                <span className="font-mono text-amber">30% EV share</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 max-w-3xl leading-relaxed">
          Numbers synthesised from public sources · ACMA · IBEF · SIAM · FADA FY26 · MoRTH Vahan · PIB · directional, not financial-grade.
        </div>
      </section>

      {/* NETWORK DENSITY */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24 bg-black border-y border-white/10">
        <SectionHeader
          kicker="The physical network"
          title="The largest retail network in the country may be automotive."
          intro="Beyond vehicles — the dealerships, workshops, and chargers that the entire ecosystem hangs off."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
          {[
            { v: '28,000+', l: 'Authorised dealer outlets', sub: 'Across PV, CV, 2W, 3W' },
            { v: '1.5M+', l: 'Workshops', sub: 'Authorised + multi-brand garages' },
            { v: '27,737', l: 'Public EV chargers', sub: 'PIB · 1 Mar 2026 · installed' },
            { v: '1,500+', l: 'RTOs', sub: 'Across all states & UTs' },
          ].map((b) => (
            <div key={b.l} className="bg-ink-900 p-7">
              <div className="font-display text-4xl text-amber tabular-nums">{b.v}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white mt-3">
                {b.l}
              </div>
              <div className="text-zinc-500 text-xs mt-1">{b.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* OPPORTUNITY */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-24">
        <SectionHeader
          kicker="Where the next decade plays out"
          title="The opportunity is not in the headline. It's in the gaps."
          intro="Three structural shifts we are watching, building for, and investing in."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              n: '01',
              t: 'EV economics tipping point',
              d: 'EV share has compounded ~70% YoY in select states. Charging networks, battery health, and used-EV pricing are the next billion-dollar layers.',
              tag: 'EV · Charging · Batteries',
            },
            {
              n: '02',
              t: 'Commercial vehicles getting smart',
              d: '~1M new CVs every year, ~10M+ on Indian roads, but most still data-dark. Telematics, finance, used CV, and aftermarket are wide open.',
              tag: 'CV · Telematics · Finance',
            },
            {
              n: '03',
              t: 'Data as common infrastructure',
              d: 'Lifecycle data — chassis, dispatch, finance, insurance, service — still lives in islands. Neutral exchanges are the next utility layer.',
              tag: 'Data · Exchanges · Standards',
            },
          ].map((b) => (
            <div
              key={b.n}
              className="relative bg-ink-800 border border-white/10 p-8 hover-trace hover:border-amber/40 transition-colors"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">/ {b.n}</span>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                  {b.tag}
                </span>
              </div>
              <h3 className="font-display text-2xl text-white mb-3">{b.t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 radial-glow-amber opacity-50" />
        <div className="relative max-w-4xl">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" />
              You are looking at the world's most dynamic auto market.
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-balance leading-[1.05]">
            If you operate in automotive — or want to —
            <span className="text-amber"> the next ten years happen here.</span>
          </h2>
          <p className="mt-6 text-zinc-300 text-base md:text-lg max-w-2xl">
            We build with this map open on every wall. If you'd like to read
            it together, let's talk.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <AtlasCTA
              label="Get the India Mobility Atlas (PDF)"
              testId="landscape-atlas-cta"
            />
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-amber bg-amber px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
            >
              Talk to us
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/platforms"
              className="group inline-flex items-center gap-2 border border-white/25 px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-white transition-colors hover:border-white hover:bg-white/5"
            >
              See the platforms behind this view
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-3 leading-relaxed">
              Sources synthesised from public MoRTH Vahan, SIAM, FADA FY26 &amp; PIB releases · numbers reflect FY26 reported figures · refreshed periodically.
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Landscape;
